import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import usePagination10 from './components/hooks/usePagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import DefaultModal from './components/modals/DefaultModal';
import LoadingModal from './components/modals/LoadingModal';
import { API_BASE_URL } from '../config/ApiConfig';

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function normalizeText(s) {
  if (!s) return '';
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export default function RelatorioReceitaPorCidade() {
  const token = localStorage.getItem('token');
  const ano = localStorage.getItem('ano');
  const [dados, setDados] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtroCidade, setFiltroCidade] = useState('');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    if (!ano) { setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.RECEITA}/porCidade`, {
      params: { ano: parseInt(ano, 10) },
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => setDados(Array.isArray(r.data) ? r.data : []))
      .catch(e => {
        console.error('Erro ao carregar receitas por cidade:', e);
        showDefaultModal('error', `Erro ao carregar receitas: ${e.response?.data?.message || e.message}`);
      })
      .finally(() => setIsLoading(false));
  }, [ano, token]);

  const cidades = useMemo(() => {
    const set = new Set(dados.map(d => d.municipio).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [dados]);

  const dadosFiltrados = useMemo(() => {
    if (!filtroCidade) return dados;
    const termo = normalizeText(filtroCidade);
    return dados.filter(d => normalizeText(d.municipio).includes(termo));
  }, [dados, filtroCidade]);

  const agrupado = useMemo(() => {
    const map = new Map();
    dadosFiltrados.forEach(d => {
      const key = `${d.municipio}|${d.uf}|${d.nomeEscola}|${d.destinacao}`;
      if (!map.has(key)) {
        map.set(key, {
          municipio: d.municipio || '',
          uf: d.uf || '',
          nomeEscola: d.nomeEscola || '',
          destinacao: d.destinacao || '',
          vlrC: 0,
          vlrK: 0,
        });
      }
      const item = map.get(key);
      item.vlrC += Number(d.vlrC || 0);
      item.vlrK += Number(d.vlrK || 0);
    });
    return Array.from(map.values()).sort((a, b) => {
      const cm = `${a.municipio}/${a.uf}`.localeCompare(`${b.municipio}/${b.uf}`, 'pt-BR');
      if (cm !== 0) return cm;
      const ce = a.nomeEscola.localeCompare(b.nomeEscola, 'pt-BR');
      if (ce !== 0) return ce;
      return a.destinacao.localeCompare(b.destinacao, 'pt-BR');
    });
  }, [dadosFiltrados]);

  const totalGeral = useMemo(() => {
    return agrupado.reduce((acc, r) => ({ vlrC: acc.vlrC + r.vlrC, vlrK: acc.vlrK + r.vlrK }), { vlrC: 0, vlrK: 0 });
  }, [agrupado]);

  const { setPagination } = usePagination10(agrupado.length);
  const displayed = agrupado.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="RECEITAS POR CIDADE" icon="bi-geo-alt" />
        <PageContent>
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <label className="form-label mb-0 small fw-semibold">Cidade:</label>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 250 }}
              value={filtroCidade}
              onChange={e => setFiltroCidade(e.target.value)}
            >
              <option value="">Todas as cidades</option>
              {cidades.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="text-muted small ms-auto">
              {agrupado.length} registro(s) | Ano: <strong>{ano}</strong>
            </span>
          </div>

          <div className="table-responsive">
            <table className="table table-hover table-sm" style={{ fontSize: '0.85rem' }}>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Cidade/UF</th>
                  <th>Escola</th>
                  <th>Destinação</th>
                  <th className="text-end">Custeio</th>
                  <th className="text-end">Capital</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((item, idx) => (
                  <tr key={idx}>
                    <td>{setPagination.startIndex + idx + 1}</td>
                    <td>{item.municipio}/{item.uf}</td>
                    <td>{item.nomeEscola}</td>
                    <td>{item.destinacao}</td>
                    <td className="text-end">{formatCurrency(item.vlrC)}</td>
                    <td className="text-end">{formatCurrency(item.vlrK)}</td>
                    <td className="text-end fw-bold">{formatCurrency(item.vlrC + item.vlrK)}</td>
                  </tr>
                ))}
                {displayed.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-3">
                      <i className="bi bi-inbox me-2"></i>Nenhuma receita encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
              {agrupado.length > 0 && (
                <tfoot className="table-light">
                  <tr className="fw-bold">
                    <td colSpan={4} className="text-end">Total Geral:</td>
                    <td className="text-end">{formatCurrency(totalGeral.vlrC)}</td>
                    <td className="text-end">{formatCurrency(totalGeral.vlrK)}</td>
                    <td className="text-end text-success">{formatCurrency(totalGeral.vlrC + totalGeral.vlrK)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <DefaultModal {...setDefaultModal} />
    </Layout>
  );
}
